/**
 * Router dei comandi toccati sul display di auto/moto (Ott 2026).
 *
 * PERCHÉ ESISTE (segnalazione 14 Ott 2026: "Ferma e salva da Android Auto non
 * salva il percorso"): i comandi dell'auto venivano consegnati con un evento
 * `window` e il comando nativo viene consumato in modo DISTRUTTIVO. Se la
 * pagina di registrazione non era montata (app riaperta a freddo dopo che
 * Android aveva ucciso la WebView, oppure utente su un'altra pagina) nessuno
 * ascoltava l'evento: il comando spariva per sempre e il giro non veniva
 * salvato, senza alcun avviso per il rider.
 *
 * COME FUNZIONA: la pagina che sa eseguire il comando (RecordRoute) si registra
 * qui al mount. Chi riceve il comando dal nativo (App) chiama
 * `dispatchCarCommand`: se nessun handler è registrato — o l'handler dichiara
 * di non poterlo eseguire — ritorna `false` e l'App applica il fallback
 * (navigare alla pagina di registrazione, che recupera la sessione da
 * IndexedDB e completa il salvataggio).
 *
 * L'handler è SINCRONO per la decisione (`true` = preso in carico, l'esito
 * arriverà poi nella pagina) perché il fallback deve partire subito.
 */

export type CarCommandHandler = (
  command: string,
  name?: string | null,
) => boolean;

let handler: CarCommandHandler | null = null;

/**
 * Registra l'handler dei comandi auto. Ritorna la funzione di annullamento
 * (da chiamare nel cleanup dell'effetto).
 */
export function registerCarCommandHandler(
  next: CarCommandHandler,
): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

/**
 * Consegna il comando all'handler registrato.
 *
 * @returns true se qualcuno lo ha preso in carico; false se nessuno può
 *   eseguirlo (il chiamante deve applicare il fallback: nessun comando va
 *   mai perso in silenzio).
 */
export function dispatchCarCommand(
  command: string,
  name?: string | null,
): boolean {
  if (!handler) return false;
  try {
    return handler(command, name) === true;
  } catch {
    // Un handler difettoso non deve bloccare il fallback
    return false;
  }
}

/** Solo per i test: azzera l'handler registrato. */
export function resetCarCommandRouterForTests(): void {
  handler = null;
}
